import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { getSupabase } from "@/lib/supabase-lazy";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeSwitcher } from "@/components/ThemeSwitcher";

import { showAccessibilityMenu } from "@/components/AccessibilityMenu";
import tfLogo from "@/assets/tf-logo-96.png.asset.json";

const GROUPS: { title: string; links: { to: string; label: string; hash?: string }[] }[] = [
  {
    title: "Product",
    links: [
      { to: "/", label: "Overview" },
      { to: "/", label: "How it works", hash: "how" },
      { to: "/pricing", label: "Pricing" },
      { to: "/founders", label: "Founders Edition" },
    ],
  },
  {
    title: "Resources",
    links: [
      { to: "/blog", label: "Blog" },
      { to: "/glossary", label: "Glossary" },
      { to: "/faq", label: "FAQ" },
      { to: "/security", label: "Security" },
    ],
  },
  {
    title: "Company",
    links: [
      { to: "/about", label: "About" },
      { to: "/method", label: "Method" },
      { to: "/legal", label: "Terms", hash: "terms" },
      { to: "/legal", label: "Privacy", hash: "privacy" },
    ],
  },

];

function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!value) return;
    setBusy(true);
    const supabase = await getSupabase();
    const { error } = await supabase.from("newsletter_subscribers").insert({ email: value });
    setBusy(false);
    if (error && !error.message.toLowerCase().includes("duplicate")) {
      toast.error("Couldn't add you just now — try again in a moment.");
      return;
    }
    setEmail("");
    toast.success("You're on the list. Quiet notes only.");
  };

  return (
    <form onSubmit={submit} className="mt-6 w-full max-w-sm">
      <label htmlFor="newsletter-email" className="micro-label">
        Dispatch
      </label>
      <div className="mt-3 flex w-full flex-col gap-2.5 sm:flex-row">
        <input
          id="newsletter-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          className="min-h-14 w-full min-w-0 flex-1 rounded-sm border border-border bg-surface px-4 py-3.5 text-base leading-snug text-foreground outline-none transition-colors placeholder:text-muted-foreground/50 focus:border-foreground/40 sm:min-h-12 sm:px-3.5 sm:text-sm"
        />
        <button
          type="submit"
          disabled={busy}
          className="micro-label min-h-14 w-full shrink-0 rounded-sm border border-border px-4 py-3.5 transition-colors hover:border-foreground/40 hover:text-foreground disabled:opacity-50 sm:min-h-12 sm:w-auto"
        >
          {busy ? "Sending" : "Join"}
        </button>
      </div>

      <p className="mt-2 text-xs text-muted-foreground/60">
        Occasional notes on building quietly. No noise, unsubscribe anytime.
      </p>
    </form>
  );
}

export function SiteFooter() {
  return (
    <footer className="relative border-t border-border/60">
      <div className="mx-auto w-full max-w-6xl px-6 py-12 sm:px-10 sm:py-14">
        <div className="mb-12 flex flex-col gap-5 rounded-sm border border-border bg-surface/50 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
          <div>
            <p className="text-lg leading-snug text-foreground">
              One move a day. Take one bold risk a month.
            </p>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Private by default. Four digits between the world and your history.
            </p>
          </div>
          <div className="flex flex-wrap gap-2.5">
            <Link
              to="/auth"
              search={{ beta: "1" }}
              className="micro-label rounded-sm border border-foreground/70 bg-foreground/10 px-4 py-3 text-foreground transition-colors hover:bg-foreground/20"
            >
              Take a free beta seat
            </Link>
            <Link
              to="/pricing"
              className="micro-label rounded-sm border border-border px-4 py-3 transition-colors hover:border-foreground/40 hover:text-foreground"
            >
              See pricing
            </Link>
          </div>
        </div>

        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-[1.4fr_repeat(3,0.6fr)]">

          <div>
            <div className="flex items-center gap-3">
              <img
                src={tfLogo.url}
                alt="The Founders App logo"
                className="h-8 w-8 rounded-sm"
                width={32}
                height={32}
              />
              <p className="micro-label text-foreground">The Founders App</p>
            </div>
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-muted-foreground">
              One move a day, per business. Locked behind four digits, seen by no one.
            </p>
            <NewsletterForm />
          </div>

          {GROUPS.map((group) => (
            <nav key={group.title}>
              <p className="micro-label">{group.title}</p>
              <ul className="mt-4 space-y-2.5">
                {group.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      {...(link.hash ? { hash: link.hash } : {})}
                      className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        <div className="hairline mt-12" />

        <p className="pt-6 text-xs leading-relaxed text-muted-foreground/70">
          Works in any browser today. Additional platforms may come later.
        </p>

        <div className="flex flex-wrap items-center gap-4 pt-6">
          <LanguageSwitcher />
          <ThemeSwitcher up />

          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event("momentum:open-cookie-settings"))}
            className="text-xs text-muted-foreground/70 underline underline-offset-4 transition-colors hover:text-foreground"
          >
            Cookie settings
          </button>
          <button
            type="button"
            onClick={() => showAccessibilityMenu()}
            className="text-xs text-muted-foreground/70 underline underline-offset-4 transition-colors hover:text-foreground"
            aria-label="Open accessibility settings"
          >
            Accessibility
          </button>
        </div>

        <div className="flex flex-wrap items-start justify-between gap-4 pt-6">
          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground/70">
              © {new Date().getFullYear()} The Founders App — a Skylab Studio LLC company.
            </p>
            <p className="text-xs text-muted-foreground/50">
              San Francisco Bay Area, California. Built for the quietly relentless.
            </p>
          </div>
          <span className="micro-label text-muted-foreground/50">No streaks. No noise.</span>
        </div>
      </div>
    </footer>
  );
}
